import React, { PureComponent, SyntheticEvent } from 'react';
import {
  PROJECT_SELECTOR_ADD_PROJECTS_CLICK,
  PROJECT_SELECTOR_CLICK,
  PROJECT_SELECTOR_SELECTION_CLICK,
  logEvent,
} from '../../utils/analytics';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import Selector, { Item, Organization } from '../../components/Selector';
import { connect } from 'react-redux';
import { selectors } from '../../reducers/projects';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { Project } from '../../models/Project';
import { Integration } from '../../models/Integration';
import { projectsHrefSelector } from '../../selectors/Organization';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { Div } from 'Common/components/Atoms/Div';
import { DividerItem } from 'Common/components/Molecules/DropMenu';
import format from 'Common/utilities/formatting/numbers';

interface GetNewProjectUrlInput {
  newProject: Project;
  project?: Project;
  organization: Organization;
  location: {
    pathname: string;
  };
}

export function getNewProjectUrl({
  newProject,
  project,
  organization,
  location,
}: GetNewProjectUrlInput): string {
  const projectBaseUrl = `/dash/projects/${organization.providerId}/${organization.slug}/`;
  let url = projectBaseUrl + encodeURIComponent(newProject.name);
  if (project && newProject.uuid === project.uuid) {
    return url;
  }

  if (project) {
    let endingPath = location.pathname.split(projectBaseUrl + project.name)[1];

    if (endingPath) {
      if (endingPath.includes('/reviews/')) {
        endingPath = '/reviews';
      }
      url += endingPath;
    }
  }

  return url;
}

export interface Props {
  project: Project;
  projects: Project[];
  organization: Organization;
  location: {
    pathname: string;
  };
  integration: Integration;
  theme: Theme;
  projectsHref: string;
}

export const PROJECTS_LIMIT = 20;
export class ProjectSelector extends PureComponent<Props> {
  addProjectsLine: Item = {
    title: 'Add Projects',
    url: '/',
    icon: faPlus,
    onClick: (e) => this.goToInstallPage(e),
  };

  getItems = (): Item[] => {
    const { projects, project, organization, location, projectsHref, theme } =
      this.props;
    if (!projects) {
      return [];
    }

    const items: Item[] = projects
      .slice(0, PROJECTS_LIMIT)
      .map((newProject) => ({
        title: newProject.name,
        url: getNewProjectUrl({ newProject, project, organization, location }),
        active: project && newProject.uuid === project.uuid,
        onClick: () => this.onClickProject(newProject),
      }));

    if (projects.length > PROJECTS_LIMIT && projectsHref) {
      items.push(DividerItem);
      items.push({
        title: `See More Projects (${format(
          projects.length - PROJECTS_LIMIT
        ).as('integer')} more)`,
        url: projectsHref,
        icon: faArrowRight,
        iconAlign: 'end',
        background: theme.palette.primary.dim,
      });
    }

    return items;
  };

  goToInstallPage = (e?: SyntheticEvent): void => {
    const { integration } = this.props;
    e?.preventDefault();
    logEvent(PROJECT_SELECTOR_ADD_PROJECTS_CLICK);
    window.location.href = integration.install_url;
  };

  onClickProject = (newProject: Project): void => {
    logEvent(PROJECT_SELECTOR_SELECTION_CLICK);
    localStorage.setItem('last_selected_project_uuid', newProject.uuid);
  };

  render(): React.ReactElement {
    const { organization, integration, theme } = this.props;
    const items = this.getItems();

    return (
      <Div ml={7}>
        <Selector
          items={items}
          title="Projects"
          id="projectSelector"
          width={400}
          onClick={() => logEvent(PROJECT_SELECTOR_CLICK)}
          footer={
            integration && organization.provider !== 'onprem'
              ? this.addProjectsLine
              : undefined
          }
        />
      </Div>
    );
  }
}

interface OwnProps {
  organization: Organization;
}

function mapStateToProps(state: any, ownProps: OwnProps) {
  const { organization } = ownProps;
  const { accountIntegrations } = state;
  const projects = selectors.projectsForOrganization(
    state,
    organization.providerId,
    organization.slug
  );
  const integration = accountIntegrations.integrations.find(
    (integration: Integration) => integration.name === organization.provider
  );

  return {
    projects,
    integration,
    projectsHref: projectsHrefSelector(state),
  };
}

export default withTheme(connect(mapStateToProps)(ProjectSelector));
